<template>
  <div class="topic">
    <div class="topic__info">
      <div class="topic__info__header">
        <label class="topic__info__header__section-number"
          >SECTION {{ order }}</label
        >
        <h4 class="topic__info__header__name">{{ topic.name }}</h4>
      </div>
      <div v-if="hasAccess && progress" class="topic__info__progress">
        <radial-progress-bar
          :diameter="30"
          :strokeWidth="3"
          :completed-steps="progress.completed"
          startColor="#00DC9B"
          :total-steps="progress.total"
        />
        <label class="topic__info__progress__label">
          {{ progressPercentage }}% complete
        </label>
      </div>
    </div>
    <ul class="topic__content-list">
      <ContentTile
        v-for="item in topic.content"
        :key="item.uuid"
        :content="getCurrentContent(item)"
        :hasAccess="hasAccess"
        :unSubscribedLink="unSubscribedLink"
        :learningAreaName="learningAreaName"
        :learningAreaColor="learningAreaColor"
        :topicIndex="topicIndex"
        :levelOpen="levelOpen"
        @openLevel="openLevel"
      />
    </ul>
  </div>
</template>

<script>
import RadialProgressBar from 'vue-radial-progress'
import { mapState } from 'vuex'
import CONSTANTS from '@/constants'
import ContentTile from './ContentTile.vue'

export default {
  name: 'Topic',
  components: {
    ContentTile,
    RadialProgressBar,
  },
  props: {
    topic: {
      type: Object,
      required: true,
    },
    order: {
      type: Number,
      required: true,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    unSubscribedLink: {
      type: String,
      required: true,
    },
    learningAreaName: {
      type: String,
      required: true,
    },
    learningAreaColor: {
      type: String,
      required: true,
    },
    levelOpen: {
      type: String,
      required: true,
    },
    topicIndex: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapState({
      currentVideos: state => state.content.currentVideos,
      currentQuizzes: state => state.content.currentQuizzes,
      currentExercises: state => state.content.currentExercises,
      currentLabs: state => state.content.currentLabs,
      currentIFrames: state => state.content.currentIFrames,
    }),
    progress() {
      const progress = { completed: 0, total: 0 }

      if (this.hasAccess) {
        for (const contentItem of this.topic.content) {
          if (contentItem.content_type === 'course') {
            progress.total += contentItem.user_status.total_videos
            progress.total += contentItem.user_status.total_quizzes
            progress.total += contentItem.user_status.total_exercises
            progress.total += contentItem.user_status.total_iframes
            progress.total += contentItem.user_status.total_labs
            progress.total += contentItem.user_status.total_links
            progress.completed += contentItem.user_status.finished_videos
            progress.completed += contentItem.user_status.finished_quizzes
            progress.completed += contentItem.user_status.finished_exercises
            progress.completed += contentItem.user_status.finished_iframes
            progress.completed += contentItem.user_status.finished_labs
            progress.completed += contentItem.user_status.finished_links
          } else {
            const current = this.getCurrentContent(contentItem)

            if (current) {
              progress.total += 1
              const { user_status } = current
              if (
                user_status &&
                user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
              )
                progress.completed += 1
            }
          }
        }
      }
      return progress
    },
    progressPercentage() {
      if (this.hasAccess && this.progress.completed > 0) {
        return Math.round((100 * this.progress.completed) / this.progress.total)
      }
      return 0
    },
  },
  methods: {
    getCurrentContent(content) {
      const { content_type, uuid } = content

      switch (content_type) {
        case 'course':
          return content
        case 'video':
          return {
            ...content,
            ...this.currentVideos.find(video => video.id === uuid),
          }
        case 'quiz':
          return {
            ...content,
            ...this.currentQuizzes.find(quiz => quiz.id === uuid),
          }
        case 'exercise':
          return {
            ...content,
            ...this.currentExercises.find(exercise => exercise.id === uuid),
          }
        case 'lab':
          return {
            ...content,
            ...this.currentLabs.find(lab => lab.id === uuid),
          }
        case 'iframe':
          return {
            ...content,
            ...this.currentIFrames.find(iframe => iframe.id === uuid),
          }
        default:
          return {
            ...content,
            user_status: CONSTANTS.CONTENT_USER_STATUS.UNSTARTED,
          }
      }
    },
    openLevel(topicIndex, levelId) {
      this.$emit('openLevel', topicIndex, levelId)
    },
  },
}
</script>

<style lang="scss" scoped>
.topic {
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  gap: 20px;
  border-bottom: 1px solid var(--tundora-gray);
  padding: 40px 0;
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
    border: none;
  }

  @include desktop {
    flex-flow: row nowrap;
  }

  &__info {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    gap: 10px;
    width: 100%;

    @include desktop {
      width: 270px;
      flex-flow: column;
    }

    &__header {
      &__section-number {
        color: var(--light-blue-gray);
        font-weight: 400;
        font-size: rem(12px);
        line-height: rem(14px);
      }

      &__name {
        margin: 0;
        font-family: var(--font-medium);
        font-size: rem(18px);
        line-height: rem(24px);

        @include mobile {
          font-family: var(--font-bold);
          font-size: rem(24px);
          line-height: rem(32px);
        }
      }
    }

    &__progress {
      display: flex;
      align-items: center;
      height: fit-content;
      gap: 10px;

      &__label {
        display: flex;
        white-space: nowrap;
      }
    }
  }
  &__content-list {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }
  &__order {
    color: var(--white);
    font-size: 2.5rem;
    margin: 0;
    background-color: var(--black);
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0;
    @include mobile {
      padding: 0 25px;
    }
  }
  &__completed {
    color: var(--white);
    margin: 0;
    margin-left: 5px;
  }
}
</style>
